import React from "react";
import { useIntl } from "react-intl";
import { useFavorites } from "@contexts/FavoritesContext";
import "./EcommerceFavoriteToggle.scss";

type Props = {
    catalogItemGuid: string | undefined | null;
    /** Default "md" is the card overlay size; "sm" fits table cells and variant headers. */
    size?: "sm" | "md";
    /** Render in normal flow (detail header, table cells) instead of as an overlay on the card image. */
    inline?: boolean;
    className?: string;
    testId?: string;
};

/**
 * Heart toggle for the ecommerce favorites list. Renders nothing unless favorites are enabled for
 * the signed-in customer. Swallows the click so it never triggers the card's detail navigation or
 * submits the surrounding add-to-cart form.
 */
export const EcommerceFavoriteToggle: React.FC<Props> = (props) => {
    const favorites = useFavorites();
    const intl = useIntl();

    if (!favorites.enabled || !props.catalogItemGuid) {
        return null;
    }

    const guid = props.catalogItemGuid;
    const isFavorite = favorites.isFavorite(guid);
    const label = intl.formatMessage({
        id: isFavorite ? "remove_from_favorites" : "add_to_favorites",
        defaultMessage: isFavorite ? "Remove from favorites" : "Add to favorites",
    });

    const stop = (e: React.SyntheticEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onClick = (e: React.MouseEvent<HTMLButtonElement>) => {
        stop(e);
        favorites.toggleFavorite(guid);
    };

    const classes = [
        "EcommerceFavoriteToggle",
        props.inline ? "EcommerceFavoriteToggle--inline" : "",
        props.size === "sm" ? "EcommerceFavoriteToggle--sm" : "",
        isFavorite ? "is-favorite" : "",
        props.className || "",
    ]
        .filter(Boolean)
        .join(" ");

    return (
        <button
            type={"button"}
            className={classes}
            aria-pressed={isFavorite}
            aria-label={label}
            title={label}
            data-testid={props.testId || "favorite-toggle"}
            onClick={onClick}
            onMouseDown={stop}>
            <i className={isFavorite ? "fas fa-heart" : "far fa-heart"} aria-hidden={true} />
        </button>
    );
};

export default EcommerceFavoriteToggle;
